import {
  CircleNotchIcon,
  FileArrowDownIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useApiMutation } from '@/modules/common';

interface ParticipantsEventActionsProps {
  eventId: string;
  onGenerateReport: () => void;
  isGeneratingReport: boolean;
}

export function ParticipantsEventActions({
  eventId,
  onGenerateReport,
  isGeneratingReport,
}: ParticipantsEventActionsProps) {
  const queryClient = useQueryClient();
  const [isRemoveExternalIdsDialogOpen, setIsRemoveExternalIdsDialogOpen] =
    useState(false);

  const openRemoveExternalIdsDialog = async () => {
    setIsRemoveExternalIdsDialogOpen(true);
  };

  const removeEventExternalIdsMutation = useApiMutation<void>(
    `/events/${eventId}/external-ids`,
    {
      method: 'DELETE',
    },
    {
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  const deleteExternalIds = async () => {
    try {
      await removeEventExternalIdsMutation.mutateAsync();
      await queryClient.invalidateQueries({ queryKey: ['eventParticipants'] });
      toast.success('External IDs removed successfully');
      setIsRemoveExternalIdsDialogOpen(false);
    } catch (error) {
      console.error(error.message);
    }
  };

  return (
    <>
      <Button
        key="generate-report"
        data-testid="generate-report-button"
        variant="transparent"
        onClick={onGenerateReport}
        startIcon={<FileArrowDownIcon />}
        isLoading={isGeneratingReport}
      >
        Generate Report
      </Button>

      <Button
        variant="destructive"
        key="remove-external-ids"
        data-testid="remove-external-ids-button"
        onClick={openRemoveExternalIdsDialog}
        startIcon={<TrashIcon weight="light" />}
        isLoading={removeEventExternalIdsMutation.isPending}
        className="text-destructive border-destructive hover:text-destructive bg-transparent"
      >
        Remove External IDs
      </Button>

      <AlertDialog
        open={isRemoveExternalIdsDialogOpen}
        onOpenChange={setIsRemoveExternalIdsDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Are you sure you want to delete all External IDs for this event?
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={removeEventExternalIdsMutation.isPending}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={removeEventExternalIdsMutation.isPending}
              onClick={deleteExternalIds}
              className="text-destructive border-destructive hover:text-destructive w-[200px]"
              data-testid="remove-external-ids-button"
            >
              {removeEventExternalIdsMutation.isPending && (
                <CircleNotchIcon className="animate-spin" />
              )}
              Remove External IDs
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
